/** @format */
import { NavMenu } from '@shopify/app-bridge-react';
import '@shopify/polaris/build/esm/styles.css';
import { BrowserRouter, Routes, Route, Link, Navigate } from "react-router-dom";
import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import { Context } from "./Context/Context.js";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
// import client from "@bigdatacloudapi/client"; // Removed - broken API
import { useAppBridge } from '@shopify/app-bridge-react';
import { chromeExtensionExpectedVersion, chromeExtensionMessageTargetID, AppName, isShopifyTestHost } from "./ShopifyConstants.js"
import { getCookie } from "doc-cookie";
import { i18 } from "./AppUtil.js";
import PageLoadingSkeleton from './CommonComponents/PageLoadingSkeleton.react.js';
import RouteTransitionIndicator from './CommonComponents/RouteTransitionIndicator.react.js';
import ShopifyPerformanceMonitor from "./CommonComponents/ShopifyPerformanceMonitor.react.js";
import ProductSearch from "./ProductSearchComponents/ProductSearch.react.js";

// 🚀 PERFORMANCE OPTIMIZATION: Route Loading Strategy
// Route modules are lazy-loaded so the embedded Admin shell can paint quickly.
// Hover and idle preloading keep common navigation responsive after first paint.

const lazyWithPreload = (factory) => {
  const Component = lazy(factory);
  Component.preload = factory;
  return Component;
};

const isDevelopment = process.env.NODE_ENV !== "production";
// /salesChannels (Channel Assistant) is hidden while still under development.
// Re-enable with REACT_APP_ENABLE_CHANNEL_ASSISTANT=true (or isShopifyTestHost) when ready to ship.
const channelAssistantEnabled = false;
// const channelAssistantEnabled = process.env.REACT_APP_ENABLE_CHANNEL_ASSISTANT === "true" || isShopifyTestHost;
const internalAdminToolsEnabled = process.env.REACT_APP_ENABLE_INTERNAL_ADMIN_TOOLS === "true";
const debugLog = (...args) => {
  if (isDevelopment) console.log(...args);
};
const debugWarn = (...args) => {
  if (isDevelopment) console.warn(...args);
};

// ===== CORE PAGES - Lazy loaded with hover/idle preload =====
const MyProducts = lazyWithPreload(() => import("./MyProductsComponents/MyProducts.react.js"));
const MyOrders = lazyWithPreload(() => import("./MyOrderComponents/MyOrders.react.js"));
const Setting = lazyWithPreload(() => import("./SettingComponents/Setting.react.js"));
const ExtensionInstructions = lazyWithPreload(() => import("./ExtensionComponents/ExtensionInstructions.react.js"));
const EventInfoModal = lazyWithPreload(() => import("./CommonComponents/EventInfoModal.react.js"));

// ===== SECONDARY PAGES - Lazy Loading =====
// Detail pages and less frequently accessed routes
const MyProductDetailPage = lazyWithPreload(() => import("./MyProductsComponents/MyProductDetailPage.react.js"));
const MyOrderDetailPage = lazyWithPreload(() => import("./MyOrderComponents/MyOrderDetailPage.react.js"));
const HelpCenter = lazyWithPreload(() => import("./HelpCenter.react.js"));
const ShopifyChannelAssistant = lazyWithPreload(() => import("./SalesChannelComponents/ShopifyChannelAssistant.react.js"));
const ChangePlan = lazyWithPreload(() => import("./SettingComponents/ChangePlan.react.js"));
const PromotionPage = lazyWithPreload(() => import("./PromotionComponents/PromotionPage.react.js"));

// Low-traffic pages - standard lazy loading
const AgentInstructions = lazyWithPreload(() => import("./AgentComponents/AgentInstructions.react.js"));
const ExternalApp = lazyWithPreload(() => import("./ExternalApp/ExternalApp.react.js"));
const OneTimePurchase = lazyWithPreload(() => import("./OneTimePurchase.react.js"));
const ShopifyOneTimePurchaseRedirect = lazyWithPreload(() => import("./ShopifyOneTimePurchaseRedirect.react.js"));
const ShopifyOneTimePurchaseSubscriptionRedirect = lazyWithPreload(() => import("./ShopifyOneTimePurchaseSubscriptionRedirect.react.js"));
const ShopifySubscriptionRedirect = lazyWithPreload(() => import("./ShopifySubscriptionRedirect.react.js"));
const AdminPage = lazyWithPreload(() => import('./CartifindAdminComponents/Admin.js'));

const createSessionId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `session-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const queryParams = new URLSearchParams(window.location.search);
const currency = queryParams.get("currency");
const rowsPerPage = queryParams.get("rowsPerPage");
const shipToCountry = queryParams.get("shipToCountry");
const language = queryParams.get("language");
const queryParamsIsLogin = queryParams.get("isLogin");
const showUpgradeToast = queryParams.get("showUpgradeToast");
const subscriptionType = queryParams.get("subscriptionType");

const getAuthRedirectFinalURL = () => {
  const currentPath = window.location.pathname?.replace(/^\/+/, "") || "";
  return currentPath.length > 0 && !/^auth\/shopify\/?$/i.test(currentPath)
    ? currentPath
    : "productSearch";
};

const GET_USER_PRODUCT_ORDER_COUNT = gql`
  query getUserProductOrderCount {
    getUserProductListCount
    getOrderListCount
  }
`;

const GET_SHOW_UPGRADE_ICON = gql`
query getShowUpgradeIcon {
  getShowUpgradeIcon
}
`;

const LOG = gql`
mutation log(
  $userEmail: String
  $eventName: String
  $eventValue: String
  $sessionID: String
  $IP: String
  $addOn: String
  $ref: String
  $platform: String
  $isPolaris: Boolean
  $shopName: String
  $functionResult: String
) {
  log(
    userEmail: $userEmail
    eventName: $eventName
    eventValue: $eventValue
    sessionID: $sessionID
    IP: $IP
    addOn: $addOn
    ref: $ref
    platform: $platform
    isPolaris: $isPolaris
    shopName: $shopName
    functionResult: $functionResult
  )
}
`;
// const IPClient = client("bdc_115288e1d5334e08b3af0269d0f021f2"); // Removed - broken API

export default function App() {
  // 🚀 OPTIMIZED: Use cache-and-network for optimal UX (cache first, then network update)
  const [fetchPolicy, setFetchPolicy] = useState('cache-and-network');
  const [hasChromeExtension, setHasChromeExtension] = useState(null);
  const [isChromeExtensionLoggedIn, setIsChromeExtensionLoggedIn] = useState(null);
  const [chromeExtensionInfo, setChromeExtensionInfo] = useState({ extensionId: null, version: null });
  const [IP, setIP] = useState("session-pending");
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|BB|PlayBook|IEMobile|Windows Phone|Kindle|Silk|Opera Mini/i.test(
    navigator.userAgent
  );

  // 🚀 OPTIMIZED: Only fetch data if not redirecting
  const shopify = useAppBridge();
  const shopName = shopify?.config?.shop || queryParams.get("shop") || localStorage.getItem("shopName") || '';
  const isLogin = getCookie('isLogin');
  const hasImmediateAuthContext = Boolean(
    queryParams.get("shop") ||
    (isLogin && isLogin.length > 0) ||
    (localStorage.getItem('token') && localStorage.getItem('shopName') && localStorage.getItem('storePlatform'))
  );

  // 🚀 CRITICAL FIX: Move auth check to useEffect to prevent blocking render
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [isAppBridgeReady, setIsAppBridgeReady] = useState(hasImmediateAuthContext);
  const didShowUpgradeToast = useRef(false);

  useEffect(() => {
    if (didShowUpgradeToast.current) return;
    if (showUpgradeToast !== "true") return;
    if (typeof shopify?.toast?.show !== "function") return;
    didShowUpgradeToast.current = true;
    shopify.toast.show(i18("Plan upgrade successful - ") + (subscriptionType || ""));
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has("showUpgradeToast")) {
        url.searchParams.delete("showUpgradeToast");
        window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
      }
    } catch (_error) {
      // Ignore history cleanup failures in embedded Admin.
    }
  }, [shopify]);

  // Detect Shopify in-app browser (mobile app/embedded webview)
  const isShopifyInAppBrowser = /Shopify Mobile|Shopify POS|Shopify/i.test(navigator.userAgent);

  // Robust redirect that works for both embedded and non-embedded contexts
  const performAuthRedirect = useCallback((url) => {
    if (isShopifyInAppBrowser) {
      // In Shopify mobile/webview, stay within the current context
      window.location.href = url;
    } else {
      // In browser within admin, break out of iframe to parent window
      try {
        if (window.top && window.top !== window) {
          window.top.location.href = url;
        } else {
          window.location.href = url;
        }
      } catch (e) {
        window.location.href = url;
      }
    }
  }, [isShopifyInAppBrowser]);

  // 🚀 OPTIMIZED: Async App Bridge initialization - waits until App Bridge is ready
  // Uses Promise-based approach with progressive polling for optimal performance
  useEffect(() => {
    let isCancelled = false;
    let rafId = null;
    let intervalId = null;

    const cleanup = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    // Async function that waits for App Bridge to be ready
    const waitForAppBridge = async () => {
      // Immediate check - if already ready, resolve immediately
      if (shopify && shopify.config && shopify.config.shop) {
        return true;
      }

      return new Promise((resolve) => {
        let rafAttempts = 0;
        let intervalAttempts = 0;
        const maxRafAttempts = 10; // ~160ms using requestAnimationFrame (10 * ~16ms)
        const maxIntervalAttempts = 100; // Additional 5s using setInterval (100 * 50ms = 5s)
        // Total max wait: ~5.2s - enough time for App Bridge to initialize
        let phase2Started = false;

        // Helper to check current App Bridge state (checks the current shopify value)
        const checkAppBridgeReady = () => {
          // Access shopify from closure - React will re-run effect if shopify changes
          return shopify && shopify.config && shopify.config.shop;
        };

        // Phase 1: Use requestAnimationFrame for ultra-fast checks (happens on every frame ~16ms)
        const checkWithRAF = function () {
          if (isCancelled) {
            cleanup();
            return;
          }

          rafAttempts++;

          // Check if App Bridge is ready (checking current value)
          if (checkAppBridgeReady()) {
            cleanup();
            resolve(true);
            return;
          }

          if (rafAttempts < maxRafAttempts) {
            // Continue with RAF for rapid checks
            rafId = requestAnimationFrame(checkWithRAF);
          } else if (!phase2Started) {
            // Phase 2: Switch to setInterval for continued checking (50ms)
            phase2Started = true;
            if (rafId) {
              cancelAnimationFrame(rafId);
              rafId = null;
            }

            intervalId = setInterval(function () {
              if (isCancelled) {
                cleanup();
                return;
              }

              intervalAttempts++;

              // Check if App Bridge is ready (checking current value)
              if (checkAppBridgeReady()) {
                cleanup();
                resolve(true);
              } else if (intervalAttempts >= maxIntervalAttempts) {
                // Final timeout after ~5.2s - resolve anyway (will check for fallback auth)
                cleanup();
                resolve(false);
              }
            }, 50); // 50ms intervals for Phase 2
          }
        };

        // Start checking with requestAnimationFrame
        rafId = requestAnimationFrame(checkWithRAF);
      });
    };

    // Execute async check
    waitForAppBridge().then((isReady) => {
      if (isCancelled) return;

      if (isReady) {
        setIsAppBridgeReady(true);
      } else {
        // App Bridge not ready after timeout - check for fallback auth
        const hasCookieAuth = isLogin && isLogin.length > 0;
        const hasTokenAuth = localStorage.getItem('token') && localStorage.getItem('shopName') && localStorage.getItem('storePlatform');

        if (hasCookieAuth || hasTokenAuth) {
          debugLog('✅ Proceeding with cookie/token auth despite App Bridge timeout');
        } else {
          debugWarn('⚠️ App Bridge not ready after ~5.2s, proceeding anyway');
        }
        setIsAppBridgeReady(true);
      }
    });

    return () => {
      isCancelled = true;
      cleanup();
    };
  }, [shopify, isLogin]);

  useEffect(() => {
    // 🚀 FAST AUTH CHECK: Non-blocking authentication validation with shop name validation
    // Only run if App Bridge is ready
    if (!isAppBridgeReady) {
      return; // Wait for App Bridge to be ready
    }

    const checkAuth = () => {
      // 🔍 SHOP NAME VALIDATION: Ensure we're comparing the correct shop
      const validateShopName = () => {
        if (!shopName) {
          debugWarn('No shop name available from App Bridge');
          // 🚀 FIX: Allow cookie-based auth or token-based auth to bypass strict validation
          // This prevents the app from getting stuck when App Bridge doesn't initialize
          const hasTokenAuth = localStorage.getItem('token') && localStorage.getItem('shopName') && localStorage.getItem('storePlatform');
          const hasCookieAuth = isLogin && isLogin.length > 0;

          if (hasTokenAuth || hasCookieAuth) {
            debugLog('✅ Allowing auth without shop name (cookie/token auth present)');
            return true; // Allow auth to proceed
          }
          return false;
        }

        // shopify.config.shop returns 'shop-name.myshopify.com'
        // extractShopName() returns just 'shop-name'
        const currentShopName = extractShopName(shopName);

        if (!currentShopName) {
          debugWarn('Invalid shop name format:', shopName);
          return false;
        }

        // Check if there's a stored shop name in localStorage for comparison
        const storedShopName = localStorage.getItem('shopName');
        if (storedShopName && storedShopName !== currentShopName && storedShopName !== shopName) {
          debugWarn('Shop name mismatch detected:', {
            current: currentShopName,
            stored: storedShopName,
            appBridge: shopName
          });
          // Clear potentially stale auth data
          localStorage.removeItem('shopName');
          sessionStorage.removeItem('queryParamsIsLogin');
          return false;
        }

        // Store the current shop name for future validation
        // For mobile authentication, preserve the full shop name format
        const existingShopName = localStorage.getItem('shopName');
        const isMobileAuth = localStorage.getItem('storePlatform') === 'shopify' && localStorage.getItem('token');

        if (isMobileAuth && existingShopName && existingShopName.includes('.myshopify.com')) {
          // Keep the mobile auth shop name (full format) for API compatibility
        } else {
          // Use extracted shop name for web authentication
          localStorage.setItem('shopName', currentShopName);
        }

        return true;
      };

      // Validate shop name first
      // 🚀 FIX: Only redirect if we don't have any auth method (cookie or token)
      if (!validateShopName()) {
        // Check if we have any auth method before redirecting
        const hasTokenAuth = localStorage.getItem('token') && localStorage.getItem('shopName') && localStorage.getItem('storePlatform');
        const hasCookieAuth = isLogin && isLogin.length > 0;

        if (hasTokenAuth || hasCookieAuth) {
          // We have auth, proceed without shop name validation
          debugLog('✅ Proceeding with auth despite shop name validation failure');
          setIsAuthChecked(true);
          return;
        }

        // Only redirect if we truly have no auth
        setShouldRedirect(true);
        // Try to extract shop name from URL or use a fallback
        const shopFromURL = queryParams.get('shop') || extractShopName(shopName) || 'unknown';
        const baseURL = `https://admin.shopify.com/store/${shopFromURL}/apps/${AppName}/auth/shopify`;
        const finalURL = getAuthRedirectFinalURL();
        const searchParams = new URLSearchParams(queryParams);
        if (finalURL) searchParams.set('finalURL', finalURL);

        requestAnimationFrame(() => {
          performAuthRedirect(`${baseURL}?${searchParams.toString()}`);
        });
        return;
      }

      // Check authentication status - include mobile token-based auth
      const hasTokenAuth = localStorage.getItem('token') && localStorage.getItem('shopName') && localStorage.getItem('storePlatform');

      if ((isLogin === null || isLogin.length === 0) &&
        queryParamsIsLogin === null &&
        sessionStorage.getItem('queryParamsIsLogin') === null &&
        !hasTokenAuth) {
        setShouldRedirect(true);
        // 🚀 OPTIMIZED: Build redirect URL efficiently
        const baseURL = `https://admin.shopify.com/store/${extractShopName(shopName)}/apps/${AppName}/auth/shopify`;
        const finalURL = getAuthRedirectFinalURL();
        const searchParams = new URLSearchParams(queryParams);
        if (finalURL) searchParams.set('finalURL', finalURL);

        // 🚀 FAST REDIRECT: Use requestAnimationFrame for smoother UX
        requestAnimationFrame(() => {
          performAuthRedirect(`${baseURL}?${searchParams.toString()}`);
        });
      } else {
        setIsAuthChecked(true);
      }
    };

    checkAuth();
  }, [isLogin, shopName, isAppBridgeReady, performAuthRedirect]);

  // 🚀 OPTIMIZED: Use lazy queries to defer non-critical data fetching
  // These queries won't block page rendering and will be triggered after auth
  const [getUserProductOrderCount, { loading, data }] = useLazyQuery(
    GET_USER_PRODUCT_ORDER_COUNT,
    {
      fetchPolicy,
      notifyOnNetworkStatusChange: true,
    }
  );

  const [getShowUpgradeIcon, { data: showUpgradeIcon }] = useLazyQuery(
    GET_SHOW_UPGRADE_ICON,
    {
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
    }
  );

  const scheduleAfterFirstPaint = useCallback((callback, timeout = 2500) => {
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(callback, { timeout });
      return () => window.cancelIdleCallback?.(idleId);
    }
    const timer = setTimeout(callback, Math.min(timeout, 1500));
    return () => clearTimeout(timer);
  }, []);

  const persistQueryParamDefaults = useCallback(() => {
    const setLocalStorageDefault = (key, value) => {
      const existingValue = localStorage.getItem(key);
      if (value !== null || existingValue === null || existingValue === 'null') {
        localStorage.setItem(key, value);
      }
    };

    try {
      setLocalStorageDefault('currency', currency);
      setLocalStorageDefault('rowsPerPage', rowsPerPage);
      setLocalStorageDefault('shipToCountry', shipToCountry);
      setLocalStorageDefault('language', language);
      if (subscriptionType !== null) {
        localStorage.setItem('subscriptionType', subscriptionType);
      }
      const existingLoginParam = sessionStorage.getItem('queryParamsIsLogin');
      if (queryParamsIsLogin !== null && (existingLoginParam === null || existingLoginParam === 'null')) {
        sessionStorage.setItem('queryParamsIsLogin', queryParamsIsLogin);
      }
    } catch (_error) {
      // Storage can be unavailable in constrained embedded contexts; query params still work in memory.
    }
  }, []);

  useEffect(() => {
    return scheduleAfterFirstPaint(persistQueryParamDefaults, 1500);
  }, [persistQueryParamDefaults, scheduleAfterFirstPaint]);

  // Trigger non-critical shell queries after the current route has had a chance to paint.
  useEffect(() => {
    if (!isAuthChecked || shouldRedirect) return undefined;

    let cancelUpgradeRefresh = () => { };
    const cancelCountRefresh = scheduleAfterFirstPaint(() => {
      getUserProductOrderCount().catch(() => { });
      cancelUpgradeRefresh = scheduleAfterFirstPaint(() => {
        getShowUpgradeIcon().catch(() => { });
      }, 4500);
    }, 2500);

    return () => {
      cancelCountRefresh();
      cancelUpgradeRefresh();
    };
  }, [isAuthChecked, shouldRedirect, getUserProductOrderCount, getShowUpgradeIcon, scheduleAfterFirstPaint]);

  useEffect(() => {
    if (!isAuthChecked || shouldRedirect) return; // Don't run if redirecting

    // 🚀 SIMPLIFIED: Use reliable fallback instead of broken IP geolocation API
    const initializeBasicInfo = () => {
      // Set a simple, reliable identifier for logging
      const timestamp = new Date().toISOString().slice(0, 10); // YYYY-MM-DD format
      const sessionId = sessionStorage.getItem('sessionId') || Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('sessionId', sessionId);

      setIP(`session-${sessionId}/${timestamp}`);
    };

    // 🚀 MINIMAL PRELOADING: Only after everything is settled
    const initializeMinimalPreloading = () => {
      // Only preload if network conditions are good and after significant delay
      const isSlowNetwork = navigator.connection &&
        (navigator.connection.effectiveType === 'slow-2g' ||
          navigator.connection.effectiveType === '2g' ||
          navigator.connection.saveData === true);

      const isLowEndDevice = navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2;

      if (isSlowNetwork || isLowEndDevice) {
        return;
      }

      // Wait for page to be fully settled before any preloading
      setTimeout(() => {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => {
            // Only preload the most critical route after the shell has settled.
            ProductSearch.preload?.().catch(() => { });
          }, { timeout: 15000 });
        }
      }, 5000); // Wait 5 seconds after auth check
    };

    initializeBasicInfo();
    initializeMinimalPreloading();
  }, [isAuthChecked, shouldRedirect]);

  // Detect Chrome Extension via postMessage (fetch to chrome-extension:// is blocked in iframes).
  // Fire automatically after auth — do not wait for a Connect/Sync button click, and re-check when
  // the tab becomes visible again so a late reload of the unpacked extension is picked up.
  useEffect(() => {
    if (!isAuthChecked || shouldRedirect) return;
    let cancelled = false;
    let inFlight = false;

    const checkExtension = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        const {
          checkChromeExtensionVersion,
          getChromeExtensionConnectionStatus,
        } = await import("./utils/chromeExtensionUtils.js");
        const { installed, isV2, extensionId, version } = await checkChromeExtensionVersion({ extensionId: chromeExtensionMessageTargetID, timeout: 3000 });
        if (cancelled) return;

        const versionOk = !(isShopifyTestHost && version && version !== chromeExtensionExpectedVersion);
        if (installed && versionOk) {
          debugLog('✅ Cartifind Chrome Extension detected', { extensionId, version, isV2 });
          setHasChromeExtension(true);
          setChromeExtensionInfo({ extensionId: extensionId || null, version: version || null });
          try {
            const status = await getChromeExtensionConnectionStatus({ extensionId: chromeExtensionMessageTargetID, timeout: 4000 });
            if (!cancelled) {
              setIsChromeExtensionLoggedIn(status?.loggedIn === true);
              setChromeExtensionInfo({
                extensionId: status?.extensionId || extensionId || null,
                version: status?.version || version || null,
              });
            }
          } catch (error) {
            if (!cancelled) {
              debugLog('⚠️ Chrome Extension status check error:', error);
              setIsChromeExtensionLoggedIn(false);
            }
          }
        } else {
          debugLog('❌ Cartifind Chrome Extension not detected');
          setHasChromeExtension(false);
          setIsChromeExtensionLoggedIn(false);
          setChromeExtensionInfo({
            extensionId: installed ? (extensionId || null) : null,
            version: installed ? (version || null) : null,
          });
        }
      } catch (error) {
        if (!cancelled) {
          debugLog('⚠️ Chrome Extension check error:', error);
          setHasChromeExtension(false);
          setIsChromeExtensionLoggedIn(false);
          setChromeExtensionInfo({ extensionId: null, version: null });
        }
      } finally {
        inFlight = false;
      }
    };

    const initialTimer = setTimeout(() => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(checkExtension, { timeout: 1500 });
      } else {
        checkExtension();
      }
    }, 400);

    const onVisibleOrFocus = () => {
      if (document.visibilityState === 'visible') {
        checkExtension();
      }
    };
    window.addEventListener('focus', onVisibleOrFocus);
    document.addEventListener('visibilitychange', onVisibleOrFocus);

    return () => {
      cancelled = true;
      clearTimeout(initialTimer);
      window.removeEventListener('focus', onVisibleOrFocus);
      document.removeEventListener('visibilitychange', onVisibleOrFocus);
    };
  }, [isAuthChecked, shouldRedirect]);

  const sessionID = React.useMemo(() => createSessionId(), []);
  const ref = queryParams.get("ref");
  const [log] = useMutation(LOG);
  const sendLog = useCallback((eventName, eventValue, addOn, functionResult = null) => {
    return log({
      variables: {
        userEmail: null,
        eventName,
        eventValue,
        sessionID,
        IP,
        addOn,
        ref,
        platform: isMobile ? 'mobile' : 'PC',
        isPolaris: true,
        shopName,
        functionResult
      },
    }).catch(() => null);
  }, [log, sessionID, IP, ref, isMobile, shopName]);

  const localProductCount = localStorage.getItem('productCount') !== null && localStorage.getItem('productCount') !== 'null' ? localStorage.getItem('productCount') : 0;
  const localOrderCount = localStorage.getItem('orderCount') !== null && localStorage.getItem('orderCount') !== 'null' ? localStorage.getItem('orderCount') : 0;
  const localRecentImportFailureReason = localStorage.getItem('recentImportFailureReason') || '';
  const [performanceMonitorActive, setPerformanceMonitorActive] = useState(false);
  const [state, setState] = useState({
    productCount: localProductCount,
    orderCount: localOrderCount,
    recentImportFailureReason: localRecentImportFailureReason,
    myProductIDList: [],
    isMobile,
    sendLog,
    hasChromeExtension,
    isChromeExtensionLoggedIn,
    chromeExtensionInfo
  });

  // Only show modal on desktop - skip mobile/tablet experiences
  const isShowEventInfoModalTimes = localStorage.getItem("isShowEventInfoModalTimes3") !== null ? Number(localStorage.getItem("isShowEventInfoModalTimes3")) : 0;
  const [eventInfoModalActive, setEventInfoModalActive] = useState(false);
  const [eventInfoModalType, setEventInfoModalType] = useState("us");
  const openTariffInfoModal = useCallback((type = "us") => {
    setEventInfoModalType(type === "eu" ? "eu" : "us");
    setEventInfoModalActive(true);
  }, []);

  useEffect(() => {
    if (!isAuthChecked || shouldRedirect) {
      setPerformanceMonitorActive(false);
      return undefined;
    }
    setPerformanceMonitorActive(true);
    return undefined;
  }, [isAuthChecked, shouldRedirect]);

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      sendLog,
      hasChromeExtension,
      isChromeExtensionLoggedIn,
      chromeExtensionInfo,
      isMobile,
      setEventInfoModalActive,
      openTariffInfoModal
    }));
  }, [sendLog, hasChromeExtension, isChromeExtensionLoggedIn, chromeExtensionInfo, isMobile, openTariffInfoModal]);

  useEffect(() => {
    if (!loading && data && data.getUserProductListCount !== null && data.getOrderListCount !== null) {
      // Update state with fresh data from network
      const productCountNumber = Number(data.getUserProductListCount);
      const orderCountNumber = Number(data.getOrderListCount);

      // ✅ FIX: Always save counts to localStorage, even if 0
      // This ensures we don't keep stale values from previous sessions
      if (productCountNumber >= 0) {
        localStorage.setItem('productCount', productCountNumber);
      }
      if (orderCountNumber >= 0) {
        localStorage.setItem('orderCount', orderCountNumber);
      }

      setState((prevState) => ({
        ...prevState,
        productCount: productCountNumber >= 0 ? productCountNumber : 0,
        orderCount: orderCountNumber >= 0 ? orderCountNumber : 0,
        sendLog,
        hasChromeExtension,
        isChromeExtensionLoggedIn,
        chromeExtensionInfo,
        isMobile,
        setEventInfoModalActive,
        openTariffInfoModal
      }));

      // Keep cache-and-network for real-time updates
      if (fetchPolicy !== 'cache-and-network') {
        setFetchPolicy('cache-and-network');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data, hasChromeExtension, isChromeExtensionLoggedIn, fetchPolicy]);

  // 🚀 NON-BLOCKING: App Bridge initialization is now lazy/async
  // The app renders immediately with skeleton states while auth happens in the background
  // This prevents the "infinite loading" issue when App Bridge takes time to initialize
  // Note: isAppBridgeReady is still used to gate lazy query execution, but NOT to block rendering

  // 🚀 EARLY RETURN: Don't render anything if redirecting
  if (shouldRedirect) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        minHeight: '100vh', // Prevent CLS
        width: '100%',
        fontSize: '14px',
        color: '#666',
        // contain: 'layout' REMOVED - breaks modal positioning!
      }}>
        Redirecting to authentication...
      </div>
    ); // Minimal render during redirect
  }

  const { productCount, orderCount } = state;
  const productCountExpression = productCount != null && productCount > 0 ? `(${productCount})` : "";
  const orderCountExpression = orderCount != null && orderCount > 0 ? `(${orderCount})` : "";

  // 🚀 PERFORMANCE: Preload lazy-loaded routes on hover for instant navigation
  const preloadRoute = (routeName) => {
    switch (routeName) {
      case 'productSearch':
        ProductSearch.preload?.();
        break;
      case 'chromeExtension':
        ExtensionInstructions.preload?.();
        break;
      case 'myProducts':
        MyProducts.preload?.();
        break;
      case 'myOrders':
        MyOrders.preload?.();
        break;
      case 'setting':
        Setting.preload?.();
        break;
      case 'helpCenter':
        HelpCenter.preload?.();
        break;
      case 'salesChannels':
        ShopifyChannelAssistant.preload?.();
        break;
      default:
        break;
    }
  };

  return (
    <Context.Provider value={{ state, setState }}>
      {performanceMonitorActive ? (
        <ShopifyPerformanceMonitor
          shopify={shopify}
          isEnabled
          sendLog={sendLog}
          activationDelayMs={0}
        />
      ) : null}
      <BrowserRouter>
        <RouteTransitionIndicator />
        <NavMenu>
          {/* 🚀 FIX: Use React Router Link components for client-side navigation (Shopify best practice) */}
          {/* First link should be home with rel="home" - Shopify requirement */}
          <Link to="/productSearch" rel="home" onMouseEnter={() => preloadRoute('productSearch')}>{i18("Product Search")}</Link>
          <Link to="/chromeExtension" onMouseEnter={() => preloadRoute('chromeExtension')}>{i18("Chrome Extension")}</Link>
          <Link to="/myProducts" onMouseEnter={() => preloadRoute('myProducts')}>{i18("My Products")} {productCountExpression}</Link>
          <Link to="/MyOrders" onMouseEnter={() => preloadRoute('myOrders')}>{i18("My Orders")} {orderCountExpression}</Link>
          {/* Hidden: /salesChannels is still under development */}
          {channelAssistantEnabled ? (
            <Link to="/salesChannels" onMouseEnter={() => preloadRoute('salesChannels')}>{i18("Channel Assistant")}</Link>
          ) : null}
          <Link to="/setting" onMouseEnter={() => preloadRoute('setting')}>{i18("Settings & Plan")}</Link>
          <Link to="/helpCenter" onMouseEnter={() => preloadRoute('helpCenter')}>{i18("Help Center")}</Link>
        </NavMenu>
        <Suspense fallback={null}>{showUpgradeIcon && showUpgradeIcon.getShowUpgradeIcon ? <PromotionPage /> : null}</Suspense>
        {eventInfoModalActive && !isMobile ? (
          <Suspense fallback={null}>
            <EventInfoModal
              isShowEventInfoModalTimes={isShowEventInfoModalTimes}
              eventInfoModalActive={eventInfoModalActive}
              setEventInfoModalActive={setEventInfoModalActive}
              isMobile={isMobile}
              tariffType={eventInfoModalType}
            />
          </Suspense>
        ) : null}
        <Routes>
          <Route
            exact
            path="/agentInstructions"
            element={
              <React.Suspense fallback={<PageLoadingSkeleton />}>
                <AgentInstructions />
              </React.Suspense>
            }
          />
          <Route
            exact
            path="/"
            element={
              <React.Suspense fallback={<PageLoadingSkeleton />}>
                <ProductSearch />
              </React.Suspense>
            }
          />
          <Route
            exact
            path="/productSearch"
            element={
              <React.Suspense fallback={<PageLoadingSkeleton />}>
                <ProductSearch />
              </React.Suspense>
            }
          />
          <Route
            exact
            path="/auth/shopify"
            element={
              <React.Suspense fallback={<PageLoadingSkeleton />}>
                <ProductSearch />
              </React.Suspense>
            }
          />
          <Route
            exact
            path="/chromeExtension"
            element={
              <React.Suspense fallback={<PageLoadingSkeleton />}>
                <ExtensionInstructions />
              </React.Suspense>
            }
          />
          <Route
            exact
            path="/myProducts"
            element={
              <React.Suspense fallback={<PageLoadingSkeleton />}>
                <MyProducts />
              </React.Suspense>
            }
          />
          <Route
            exact
            path="/importByURL"
            element={
              <React.Suspense fallback={<PageLoadingSkeleton />}>
                <MyProducts isImportByURL={true} />
              </React.Suspense>
            }
          />
          <Route
            exact
            path="/myProducts/:id"
            element={
              <React.Suspense fallback={<PageLoadingSkeleton />}>
                <MyProductDetailPage />
              </React.Suspense>
            }
          />
          <Route
            exact
            path="/externalApp"
            element={
              <React.Suspense fallback={<PageLoadingSkeleton />}>
                <ExternalApp />
              </React.Suspense>
            }
          />
          <Route
            exact
            path="/MyOrders"
            element={
              <React.Suspense fallback={<PageLoadingSkeleton />}>
                <MyOrders />
              </React.Suspense>
            }
          />
          {/* Hidden: /salesChannels is still under development — redirect until Channel Assistant ships */}
          <Route
            exact
            path="/salesChannels"
            element={channelAssistantEnabled ? (
              <React.Suspense fallback={<PageLoadingSkeleton />}>
                <ShopifyChannelAssistant />
              </React.Suspense>
            ) : <Navigate to="/myProducts" replace />}
          />
          {/* Legacy alias; also hidden while /salesChannels is under development */}
          <Route
            exact
            path="/salesApps"
            element={<Navigate to={channelAssistantEnabled ? "/salesChannels" : "/myProducts"} replace />}
          />
          <Route
            exact
            path="/MyOrders/:orderNumber"
            element={
              <React.Suspense fallback={<PageLoadingSkeleton />}>
                <MyOrderDetailPage />
              </React.Suspense>
            }
          />
          <Route
            exact
            path="/MyOrdersDetail"
            element={
              <React.Suspense fallback={<PageLoadingSkeleton />}>
                <MyOrderDetailPage />
              </React.Suspense>
            }
          />
          <Route
            exact
            path="/setting"
            element={
              <React.Suspense fallback={<PageLoadingSkeleton />}>
                <Setting />
              </React.Suspense>
            }
          />
          <Route
            path="/oneTimePurchase"
            element={
              <React.Suspense fallback={<PageLoadingSkeleton />}>
                <OneTimePurchase />
              </React.Suspense>
            }
          />
          <Route
            path="/helpCenter"
            element={
              <React.Suspense fallback={<PageLoadingSkeleton />}>
                <HelpCenter />
              </React.Suspense>
            }
          />
          <Route
            path="/shopifyPurchaseRedirect"
            element={
              <React.Suspense fallback={<PageLoadingSkeleton />}>
                <ShopifyOneTimePurchaseRedirect />
              </React.Suspense>
            }
          />
          <Route
            path="/shopifyOneTimePurchaseSubscriptionRedirect"
            element={
              <React.Suspense fallback={<PageLoadingSkeleton />}>
                <ShopifyOneTimePurchaseSubscriptionRedirect />
              </React.Suspense>
            }
          />
          <Route
            path="/shopifySubscriptionRedirect"
            element={
              <React.Suspense fallback={<PageLoadingSkeleton />}>
                <ShopifySubscriptionRedirect />
              </React.Suspense>
            }
          />
          <Route
            path="/changePlan"
            element={
              <React.Suspense fallback={<PageLoadingSkeleton />}>
                <ChangePlan />
              </React.Suspense>
            }
          />
          {internalAdminToolsEnabled ? (
            <Route
              path="/CartifindAdmin"
              element={
                <React.Suspense fallback={<PageLoadingSkeleton />}>
                  <AdminPage isShowPopup={false} currentPopup={null} />
                </React.Suspense>
              }
            />
          ) : null}
        </Routes>
      </BrowserRouter>
    </Context.Provider >
  );
}

function extractShopName(url) {
  if (!url || typeof url !== 'string') {
    return "";
  }

  const shopDomain = '.myshopify.com';

  // Check if the URL contains ".myshopify.com"
  if (url.includes(shopDomain)) {
    // Extract the part before ".myshopify.com"
    const shopName = url.split(shopDomain)[0];

    // Remove any protocol or subdomain prefixes (e.g., https://, www.)
    const cleanShopName = shopName.replace(/^https?:\/\//, '').replace(/^www\./, '');

    // Validate the shop name format (should only contain alphanumeric characters and hyphens)
    if (/^[a-zA-Z0-9-]+$/.test(cleanShopName)) {
      return cleanShopName;
    }
  }

  return ""; // Return "" if it's not a valid Shopify domain
}
